/**
 * API client for the push-campaign endpoints.
 *
 * Backend: campaignManager Lambda exposed via a Function URL (NOT API GW).
 * The URL is published in amplify_outputs.json under
 * `custom.CampaignManager.functionUrl`. We lazy-load it on first request and
 * cache the resolved axios client.
 *
 * The API key is held in localStorage under `add2wallet_api_key`. Lambda
 * validates the X-Api-Key header against its configured value (set
 * out-of-band on the Lambda env var). Cognito is the right end-state —
 * this keeps the key off the JS bundle in the meantime.
 *
 * Lambda contract: amplify/functions/campaignManager/handler.ts.
 * Schema: docs/DATA_CONTRACTS.md §5.
 */

import axios, { AxiosInstance } from "axios";

export const API_KEY_STORAGE_KEY = "add2wallet_api_key";

function getApiKey(): string {
  return localStorage.getItem(API_KEY_STORAGE_KEY) || "";
}

export function setApiKey(key: string): void {
  localStorage.setItem(API_KEY_STORAGE_KEY, key);
}

export function clearApiKey(): void {
  localStorage.removeItem(API_KEY_STORAGE_KEY);
}

export function hasApiKey(): boolean {
  return Boolean(getApiKey());
}

// Cache for the resolved Function URL — fetched once from amplify_outputs.json.
let resolvedBaseUrl: string | null = null;
let baseUrlPromise: Promise<string> | null = null;

async function resolveBaseUrl(): Promise<string> {
  if (resolvedBaseUrl) return resolvedBaseUrl;
  if (!baseUrlPromise) {
    baseUrlPromise = fetch("/amplify_outputs.json")
      .then((r) => r.json())
      .then((outputs) => {
        const url: string | undefined =
          outputs?.custom?.CampaignManager?.functionUrl;
        if (!url) {
          throw new Error(
            "Could not find custom.CampaignManager.functionUrl in amplify_outputs.json — has the sandbox deployed?"
          );
        }
        // Function URLs end with / — strip so axios path joining is predictable.
        resolvedBaseUrl = url.replace(/\/+$/, "");
        return resolvedBaseUrl;
      });
  }
  return baseUrlPromise;
}

async function client(): Promise<AxiosInstance> {
  const baseURL = await resolveBaseUrl();
  return axios.create({
    baseURL,
    headers: {
      "X-Api-Key": getApiKey(),
      "Content-Type": "application/json",
    },
    timeout: 30000,
  });
}

// ----- types — mirror docs/DATA_CONTRACTS.md §5 -----

export type CampaignStatus =
  | "draft"
  | "approved"
  | "sending"
  | "sent"
  | "cancelled"
  | "held"
  | "failed";

export type Platform = "apple" | "google" | "both";

export interface FieldUpdate {
  label: string;
  value: string;
  changeMessage?: string;
}

export interface CampaignPayload {
  position_header?: FieldUpdate;
  position_one?: FieldUpdate;
  position_two?: FieldUpdate;
  banner_path?: string;
  tags?: Array<FieldUpdate & { key?: string }>;
}

export interface AudienceSpecMerchantAll {
  merchant_id: string;
  campaign_id?: string;
}

export interface AudienceSpecKeywords {
  keywords: string[];
}

export type AudienceSpec = AudienceSpecMerchantAll | AudienceSpecKeywords;

export interface Campaign {
  campaign_id: string;
  merchant_id: string;
  name: string;
  audience_type: "merchant_all" | "keywords";
  /** JSON-stringified AudienceSpec. */
  audience_spec: string;
  /** JSON-stringified CampaignPayload. */
  payload: string;
  platform: Platform;
  scheduled_at: string; // ISO 8601
  status: CampaignStatus;
  created_at: string;
  created_by: string;
  target_count?: number;
  sent_count?: number;
  failed_count?: number;
  started_at?: string;
  completed_at?: string;
  last_error?: string;
}

export interface CreateCampaignInput {
  merchant_id: string;
  name: string;
  audience_type: "merchant_all" | "keywords";
  audience_spec: AudienceSpec;
  payload: CampaignPayload;
  platform: Platform;
  scheduled_at: string;
  created_by?: string;
}

export interface CampaignDevice {
  campaign_id: string;
  keyword: string;
  status: "pending" | "sent" | "failed";
  platform: Platform;
  merchant_id: string;
  sent_at?: string;
  error?: string;
  created_at?: string;
}

// ----- methods -----

export async function createCampaign(
  input: CreateCampaignInput
): Promise<{ campaign_id: string; status: CampaignStatus }> {
  // The Lambda expects audience_spec and payload as JSON-stringified strings.
  // It's strict — won't coerce arrays/objects on its end.
  const body = {
    ...input,
    audience_spec: JSON.stringify(input.audience_spec),
    payload: JSON.stringify(input.payload),
  };
  const res = await (await client()).post<{
    campaign_id: string;
    status: CampaignStatus;
  }>("", body);
  return res.data;
}

export async function listCampaigns(
  merchantId: string,
  status?: CampaignStatus
): Promise<{ items: Campaign[]; count: number }> {
  const params: Record<string, string> = { merchant_id: merchantId };
  if (status) params.status = status;
  const res = await (await client()).get<{ items: Campaign[]; count: number }>("", {
    params,
  });
  return res.data;
}

export async function getCampaign(campaignId: string): Promise<Campaign> {
  const res = await (await client()).get<Campaign>(`/${campaignId}`);
  return res.data;
}

export async function updateCampaign(
  campaignId: string,
  patch: Partial<CreateCampaignInput>
): Promise<{ campaign_id: string; updated: boolean }> {
  const body: Record<string, unknown> = { ...patch };
  if (patch.audience_spec) body.audience_spec = JSON.stringify(patch.audience_spec);
  if (patch.payload) body.payload = JSON.stringify(patch.payload);
  const res = await (await client()).post(`/${campaignId}`, body);
  return res.data;
}

export async function deleteCampaign(campaignId: string): Promise<{
  campaign_id: string;
  deleted: boolean;
}> {
  const res = await (await client()).delete(`/${campaignId}`);
  return res.data;
}

type TransitionResult = { campaign_id: string; status: CampaignStatus };

export async function approveCampaign(
  campaignId: string
): Promise<TransitionResult & { target_count: number }> {
  const res = await (await client()).post(`/${campaignId}/approve`);
  return res.data;
}

export async function holdCampaign(
  campaignId: string
): Promise<TransitionResult> {
  const res = await (await client()).post(`/${campaignId}/hold`);
  return res.data;
}

export async function resumeCampaign(
  campaignId: string
): Promise<TransitionResult> {
  const res = await (await client()).post(`/${campaignId}/resume`);
  return res.data;
}

export async function cancelCampaign(
  campaignId: string
): Promise<TransitionResult> {
  const res = await (await client()).post(`/${campaignId}/cancel`);
  return res.data;
}

// Helper to JSON-parse the embedded JSON fields safely.
export function decodeAudienceSpec(c: Campaign): AudienceSpec | null {
  try {
    return JSON.parse(c.audience_spec);
  } catch {
    return null;
  }
}

export function decodePayload(c: Campaign): CampaignPayload | null {
  try {
    return JSON.parse(c.payload);
  } catch {
    return null;
  }
}
